import React, { useEffect, useState } from 'react';
import { getCurrentUser } from '@features/auth';

import {
  type DispositionRecord,
  type SelectorDispositionObj,
  type Disposition,
  TendrilDefaultActions,
  transformSalesforceMapping
} from '@features/Settings/pages/integrationManagementPages/utils/saleforceUtils.ts';
import { type SalesforceAccount, type SalesforceDispositionTransformed, type SalesforceCallDisposition } from '@types';
import { SalesforceDispositionsApi } from '@features/Settings/hooks/useSalesforceDispositions.Api.ts';
import { Card, CardContent, CardHeader } from '@core-ui/Components/ui/card.tsx';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@core-ui/Components/ui/table.tsx';
import { InputSelect, InputSelectTrigger } from '@ui/shared/customSelect.tsx';
import { IconArrowRight, IconChevronDown } from '@tabler/icons-react';
import { Button } from '@core-ui/Components/ui/button.tsx';
import type { DispositionAbbreviation } from '@types/dispositions/dispositionEnums.ts';
import { logSuccess } from '@lib/logger/logSuccess.tsx';
import { useTranslation } from 'react-i18next';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@core-ui/Components/ui/collapsible.tsx';

interface mappingProps {
  salesforceAccounts: SalesforceAccount[];
}

export function SalesforceMapping ( { salesforceAccounts, }: mappingProps): React.JSX.Element {
  const currentUser = getCurrentUser();
  const { t } = useTranslation();
  const { getSalesforceMappingsByOrgId, getCallDispositions, saveSalesforceMapping } = SalesforceDispositionsApi();
  const [mappingsForm, setMappingsForm] = useState<Map<DispositionAbbreviation, string>>(new Map<DispositionAbbreviation, string>);
  const [dispositionWasChanged, setDispositionWasChanged] = useState(false);
  const [mappingSalesforce, setMappingSalesforce] = useState<SalesforceDispositionTransformed[]>([]);
  const [selectorDisposition, setSelectorDisposition] = useState<SelectorDispositionObj[]>([]);
  const [mappingId, setMappingId] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchMappings = async (): Promise<void> => {
    if (currentUser == null) {
      throw new Error('No current user found in session store.');
    }

    setIsAdmin(currentUser.isAdmin);

    if (salesforceAccounts.length === 0) {
      setMappingSalesforce([]);
      return;
    }

    const nonce = salesforceAccounts[0]?.nonce;
    const res = await getSalesforceMappingsByOrgId(currentUser.organizationId);

    if (res.data === null || res.data?.mapping === undefined) {
      throw new Error('error data');
    }
    const resCallbackDisposition = await getCallDispositions(currentUser.organizationId, nonce);

    if (resCallbackDisposition.data === undefined) {
      throw new Error('error data');
    }

    const dataCallDispositions: SalesforceCallDisposition[] = resCallbackDisposition.data.map((disposition, index) => {
      return {
        id: String(index),
        attributes: {
          name: disposition,
          outcome: disposition,
        },
        type: 'callDisposition',
      };
    });

    const callDispositionsRes = JSON.parse(res.data.mapping) as DispositionRecord;
    setMappingId(res.data.id);

    const transformedMapping = transformSalesforceMapping(
      callDispositionsRes,
      dataCallDispositions,
    );

    fetchSelector(dataCallDispositions);

    const updatedmap = new Map<DispositionAbbreviation, string>();
    updatedmap.set('default_action' as DispositionAbbreviation, 'default_action');

    transformedMapping.forEach((disposition) => {
      updatedmap.set(disposition.abbreviation as DispositionAbbreviation, disposition.customName);
    });

    setMappingsForm(updatedmap);

    setMappingSalesforce(removeDefaultDisposition(transformedMapping));
  };

  function removeDefaultDisposition (mappings: Disposition[]): Disposition[] {
    return mappings.filter((mapping) => !mapping.defaultName.includes('default'));
  }

  const fetchSelector = (callDispositions: SalesforceCallDisposition[]): void => {
    const responseDispositions =  [];
    callDispositions.forEach(disposition => {
      responseDispositions.push({
        value: disposition.attributes.outcome,
        label: disposition.attributes.outcome,
      });
    });

    for (const [value] of Object.entries(TendrilDefaultActions)) {
      if (value.toString() !== 'default_action') {
        if (callDispositions.find((disposition) => value.toString() === disposition.attributes.name) == null) {
          responseDispositions.push({
            value: value.toString(),
            label: value.toString(),
          });
        }
      }
    }

    setSelectorDisposition(responseDispositions);
  };

  const handleSaveMapping = async (): Promise<void> => {
    await saveSalesforceMapping(
      mappingId,
      { mapping: JSON.stringify(Object.fromEntries(mappingsForm)) }
    );

    setDispositionWasChanged(false);

    await logSuccess(
      'Salesforce Mapping was updated',
      'OrganizationsTab.fetchOrganizations',
      { mapping: mappingSalesforce },
      true,
      true,
    );

    await fetchMappings();
  };

  useEffect(() => {
    void fetchMappings();
  }, [salesforceAccounts]);

  return (
    <Collapsible defaultOpen>
      <Card className="p-6 gap-1 rounded-2xl shadow-lg border border-border bg-card text-foreground">
        <CardHeader className="items-start w-full mb-2 px-0">
          <div className="flex items-center justify-between w-full">
            <h2 className='text-lg md:text-xl font-semibold'>{t('settings.integrationsPages.mappings.mappingsTitle')}</h2>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="icon">
                <IconChevronDown className="size-4 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
              </Button>
            </CollapsibleTrigger>
          </div>
          <h3 className="text-sm">{t('settings.integrationsPages.mappings.mappingsSubtitle')}</h3>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="grid p-0">
            {isAdmin && (
              <div className="flex w-full justify-end mb-2">
                <Button
                  onClick={() => {
                    void handleSaveMapping();
                  }}
                  disabled={!dispositionWasChanged}
                  data-qa-id="save-mapping-button"
                  className="w-full sm:w-fit px-3 py-1"
                >
                  {t('settings.integrationsPages.mappings.saveDisposition')}
                </Button>
              </div>
            )}
            <div title="mappings-listing" className="w-full overflow-x-auto">
              <Table className="w-full">
                <TableHeader className="hidden md:table-header-group sticky top-0">
                  <TableRow>
                    <TableHead className="text-foreground p-0">
                      <div className="rounded-l-md bg-muted p-2">
                        {t('settings.integrationsPages.mappings.table.tendrilDisposition')}
                      </div>
                    </TableHead>
                    <TableHead className="text-foreground p-0 w-12">
                      <div className="bg-muted p-2 text-center">
                        {t('settings.integrationsPages.mappings.table.mappedTo')}
                      </div>
                    </TableHead>
                    <TableHead className="text-foreground p-0 text-right">
                      <div className="rounded-r-md bg-muted p-2 text-right">
                        {t('settings.integrationsPages.mappings.table.customDisposition')}
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="border-0">
                  {mappingSalesforce.map((mapping, index) => (
                    <TableRow
                      key={`mapping-row-${index}`}
                      className="block md:table-row border border-border/60 rounded-lg mb-2 p-2 md:border-0 md:rounded-none md:mb-0 md:p-0 md:border-t-0 hover:bg-transparent"
                    >
                      <td className="flex justify-between items-center py-1.5 md:table-cell md:px-3 md:py-3 md:whitespace-nowrap">
                        <span className="text-xs font-medium text-muted-foreground md:hidden">
                          {t('settings.integrationsPages.mappings.table.tendrilDisposition')}
                        </span>
                        {mapping.defaultAction}
                      </td>
                      <td className="hidden md:table-cell px-3 py-3 text-center">
                        <IconArrowRight className="size-4 text-muted-foreground" />
                      </td>
                      <td className="flex justify-between items-center py-1.5 md:table-cell ">
                        <span className="text-xs font-medium text-muted-foreground md:hidden">
                          {t('settings.integrationsPages.mappings.table.customDisposition')}
                        </span>
                        <div className="flex justify-end">
                          <div className="w-full md:w-3/4">
                            {isAdmin ? (
                              <InputSelect
                                placeholder={mapping.customName ?? mapping.defaultName}
                                options={selectorDisposition}
                                onValueChange={(val: string) => {
                                  const updatedMap = new Map(mappingsForm);
                                  updatedMap.set(mapping.abbreviation, val);
                                  setMappingsForm(updatedMap);
                                  setDispositionWasChanged(true);
                                }}
                                clearable={true}
                                value={'' ?? mappingsForm?.get(mapping.abbreviation)}
                              >
                                {({
                                  options,
                                  placeholder,
                                  clearable,
                                  selectedValue,
                                  isPopoverOpen,
                                  setIsPopoverOpen,
                                  onClearAllOptions,
                                }) => (
                                  <InputSelectTrigger
                                    options={options}
                                    placeholder={placeholder}
                                    clearable={clearable}
                                    disabled={options === null}
                                    selectedValue={selectedValue}
                                    isPopoverOpen={isPopoverOpen}
                                    setIsPopoverOpen={setIsPopoverOpen}
                                    onClearAllOptions={onClearAllOptions}
                                    className="rounded-md px-3 py-2 text-left bg-muted hover:bg-muted/50 border w-full"
                                  />
                                )}
                              </InputSelect>
                            ) : (
                              <span>{mapping.customName}</span>
                            )}
                          </div>
                        </div>
                      </td>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}